import {
  Box,
  Card,
  CardContent,
  CardMedia,
  ScrollView,
  ScrollViewItem,
  Typography,
} from '@ace-clublabs/shared-component-library';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import type { Destination } from '@/features/destinations/utils/Destination';
import { getDestinationRegionLabel } from '@/features/destinations/utils/DestinationRegionLabelMapper';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';
import { Page } from '@/lib/tagging';
import useTagging from '@/lib/tagging/useTagging';
import {
  type DestinationCardSection,
  getDestinationCardCta,
} from '@/lib/tagging/utils';
import useAppointmentStore from '@/store/AppointmentStore';
import useSessionStore from '@/store/SessionStore';
import {
  getRelevanceSearch,
  relevanceSearchQueryKey,
} from '../api/getRelevanceSearch';
import useFeaturedPopularDestinations from '../hooks/useFeaturedPopularDestinations';
import type { DestinationCardData } from '../types/destinationCards';
import type { RelevanceSearchResult } from '../types/RelevanceSearchResult';

interface DestinationCardProps {
  destination: DestinationCardData;
  onClick: (destination: DestinationCardData) => void;
}

function DestinationCard({ destination, onClick }: DestinationCardProps) {
  return (
    <Card
      role='button'
      tabIndex={0}
      onClick={() => onClick(destination)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick(destination);
        }
      }}
      sx={{
        position: 'relative',
        width: 200,
        height: 220,
        flexShrink: 0,
        borderRadius: 3,
        cursor: 'pointer',
        overflow: 'hidden',
        '&:hover .MuiCardMedia-root': {
          transform: 'scale(1.05)',
        },
      }}
      data-testid={`destination-card-${destination.name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '')}`}
    >
      <CardMedia
        image={destination.image}
        title={destination.name}
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          transition: 'transform 0.3s ease-in-out',
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            borderRadius: 'inherit',
            background:
              'linear-gradient(180deg, rgba(102, 102, 102, 0.50) 43.64%, rgba(0, 0, 0, 0.50) 63.64%)',
          },
        }}
      />
      <CardContent
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          p: 1.5,
          '&:last-child': { pb: 1.5 },
          display: 'flex',
          flexDirection: 'column',
          zIndex: 1,
        }}
      >
        <Typography variant='h5' sx={{ color: '#fff', lineHeight: 1.2 }}>
          {destination.name}
        </Typography>
      </CardContent>
    </Card>
  );
}

interface DestinationSectionProps {
  title: string;
  destinations: DestinationCardData[];
  onCardClick: (destination: DestinationCardData) => void;
}

function DestinationSection({
  title,
  destinations,
  onCardClick,
}: DestinationSectionProps) {
  return (
    <Box sx={{ width: '100%', maxWidth: 1280, mx: 'auto' }}>
      <Typography
        variant='h5Medium'
        sx={{
          color: '#0B3E6F',
          textShadow:
            '0 -2px 4px rgba(8, 17, 34, 0.04), 0 8px 16px rgba(8, 17, 34, 0.20)',
          mb: 2,
        }}
      >
        {title}
      </Typography>
      <ScrollView
        spacing='12px'
        scrollDistance={75}
        sx={{
          '& .MuiIconButton-root': {
            backgroundColor: '#016BCD',
            color: '#fff',
            width: 36,
            height: 36,
            zIndex: 3,
            display: { xs: 'none', md: 'flex' },
            '&:hover': { backgroundColor: '#0155a4' },
          },
        }}
      >
        {destinations.map((dest) => (
          <ScrollViewItem key={dest.name} sx={{ width: 'auto' }}>
            <DestinationCard destination={dest} onClick={onCardClick} />
          </ScrollViewItem>
        ))}
      </ScrollView>
    </Box>
  );
}

interface DestinationCardsProps {
  proceedWithDestination: (destination: Destination) => void;
}

export default function DestinationCards({
  proceedWithDestination,
}: DestinationCardsProps) {
  const destinations = useAppointmentStore((state) => state.destinations);
  const setSelectedDestination = useAppointmentStore(
    (state) => state.setSelectedDestination,
  );
  const setSearchedDestination = useAppointmentStore(
    (state) => state.setSearchedDestination,
  );
  const useGoogleApiForRBS = useFeatureFlag('useGoogleApiForRBS');
  const { featured, popular } = useFeaturedPopularDestinations();
  const queryClient = useQueryClient();
  const tagging = useTagging();
  const { sessionId, sessionSource, flowType } = useSessionStore();
  const isEmailFlow = flowType === 'emailInquiry';

  const fireDestinationCardTag = useCallback(
    (card: DestinationCardData, section: DestinationCardSection) => {
      if (!sessionId || !sessionSource) return;

      tagging.tag('link', sessionSource, {
        page: Page.destination,
        sessionid: sessionId,
        flow: flowType || '',
        cta: getDestinationCardCta(section, card.name),
        ...(isEmailFlow && { comm_pref: 'email' }),
      });
    },
    [sessionId, sessionSource, flowType, isEmailFlow, tagging.tag],
  );

  const handleCardClick = useCallback(
    async (card: DestinationCardData, section: DestinationCardSection) => {
      fireDestinationCardTag(card, section);

      let searchResults: RelevanceSearchResult[];
      try {
        searchResults = await queryClient.fetchQuery({
          queryKey: relevanceSearchQueryKey(
            card.searchTerm,
            useGoogleApiForRBS,
          ),
          queryFn: () =>
            getRelevanceSearch(
              card.searchTerm,
              useGoogleApiForRBS,
            ),
          staleTime: Number.POSITIVE_INFINITY,
        });
      } catch {
        return;
      }

      if (!searchResults.length) return;

      const topResult = searchResults[0];

      const matchedDestination = destinations.find(
        (dest) => dest.name === topResult.aceDestination,
      );

      if (!matchedDestination) return;

      const regionLabel = getDestinationRegionLabel(matchedDestination.name);

      const mappedDestination: Destination = {
        ...matchedDestination,
        name: regionLabel,
      };

      setSelectedDestination(mappedDestination);
      setSearchedDestination(card.name, { fromCard: true });
      proceedWithDestination(mappedDestination);
    },
    [
      fireDestinationCardTag,
      destinations,
      useGoogleApiForRBS,
      queryClient,
      setSelectedDestination,
      setSearchedDestination,
      proceedWithDestination,
    ],
  );

  return (
    <Box
      sx={{
        width: '100%',
        height: { xs: 'auto', md: '731px' },
        px: { xs: 2, md: 4 },
        py: 4,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: 4,
      }}
      data-testid='destination-cards-section'
    >
      <DestinationSection
        title='Featured destinations'
        destinations={featured}
        onCardClick={(destination) => handleCardClick(destination, 'featured')}
      />
      <DestinationSection
        title='Popular destinations'
        destinations={popular}
        onCardClick={(destination) => handleCardClick(destination, 'popular')}
      />
    </Box>
  );
}
